import {Button, Group, Stack, Text} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {ROUTINE_EXAMPLES, RoutineExample} from './routineExamples';
import {useRoutineSectionHeaderStyles} from './routineStyles';
import {TriggerType} from './RoutineTypes';

interface RoutineExamplesSectionProps {
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
  onSelectExample: (example: RoutineExample) => void;
  disabled?: boolean;
}

export default function RoutineExamplesSection({
  timeBasedEnabled,
  eventBasedEnabled,
  onSelectExample,
  disabled = false,
}: RoutineExamplesSectionProps): JSX.Element | null {
  const {classes} = useRoutineSectionHeaderStyles();
  // Explicitly map each trigger type to its enabled flag so a newly added trigger type must be
  // handled here rather than silently defaulting to "enabled".
  const enabledByTriggerType: Record<TriggerType, boolean> = {
    [TriggerType.TIME_BASED]: timeBasedEnabled,
    [TriggerType.EVENT_BASED]: eventBasedEnabled,
  };
  // Only surface examples whose trigger type is enabled, so we never prefill a disabled trigger type.
  const examples = ROUTINE_EXAMPLES.filter((example) => {
    const {triggerType} = example.formState;
    return triggerType != null && enabledByTriggerType[triggerType];
  });

  if (examples.length === 0) {
    return null;
  }

  return (
    <Stack spacing="sm">
      <Text className={classes.sectionHeader}>Get started with an example</Text>
      <Group spacing="sm">
        {examples.map((example) => (
          <Button
            key={example.id}
            variant="secondary"
            radius="xl"
            size="sm"
            leftIcon={<BenchlingIcon icon={example.icon} aria-hidden={true} />}
            disabled={disabled}
            onClick={() => {
              onSelectExample(example);
            }}
          >
            {example.label}
          </Button>
        ))}
      </Group>
    </Stack>
  );
}
